import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react"; // Use the named export
import RowActionsMenu from '../../Components/RowActionsMenu';
import { missingLicenses } from '../../Utils/userAuthority';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [qrCodeData, setQrCodeData] = useState(""); // State to store QR code data
    const navigate = useNavigate();
    const [genusers, setGenUsers] = useState("");
    const [catalogRoles, setCatalogRoles] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState("ALL");
    const handleLogout = useCallback(() => {
        localStorage.removeItem("user");
        navigate("/login");
    }, [navigate]);

    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("user")) || null;
        } catch {
            return null;
        }
    }, []);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem("user");
        if (!user) {
            handleLogout();
            return null;
        }
        return JSON.parse(user)?.token;
    }, [handleLogout]);

    const fetchUserData = useCallback(async () => {
        try {
            const token = checkToken();
            if (!token) return;

            const [response, catalogResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/auth/api/main/v4/users`, {
                    method: "GET",
                    headers: {
                        accept: "*/*",
                        Authorization: `Bearer ${token}`,
                    },
                }),
                fetch(`${API_BASE_URL}/auth/api/main/v4/catalog`, {
                    method: "GET",
                    headers: {
                        accept: "*/*",
                        Authorization: `Bearer ${token}`,
                    },
                }),
            ]);

            if (!response.ok) {
                if (response.status === 403) {
                    handleLogout();
                    return;
                }
                throw new Error("Failed to fetch user data");
            }
            if (!catalogResponse.ok) {
                if (catalogResponse.status === 403) {
                    handleLogout();
                    return;
                }
                throw new Error("Failed to fetch catalog data");
            }

            const result = await response.json();
            const catalogResult = await catalogResponse.json();
            console.log("API response:", result); // Debugging line

            if (Array.isArray(result)) {
                setUsers(result);
            } else if (result && Array.isArray(result.data)) {
                setUsers(result.data);
            } else {
                throw new Error("Data is not an array");
            }

            setCatalogRoles(Array.isArray(catalogResult?.roles) ? catalogResult.roles : []);
        } catch (error) {
            setError("An error occurred while fetching user data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    }, [checkToken, handleLogout]);

    const generateQrCode = async (userId , genUsers , nameuser) => {
        try {
            const token = checkToken();
            if (!token) return;

            const response = await fetch(
                `${API_BASE_URL}/auth/api/main/tokens/same-or-lower/${userId}`,
                {
                    method: "POST",
                    headers: {
                        accept: "*/*",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const body = await response.json().catch(() => null);
                setError(body?.message || `สร้าง QR ไม่สำเร็จ (HTTP ${response.status})`);
                return;
            }

            const result = await response.json();
            console.log("Generated token:", result); // Debugging line


            if (result.token) {
                setError("");
                setQrCodeData(result.token); // Set the token as QR code data
                const showQr = genUsers + ' [' + nameuser + ']';
                setGenUsers(showQr);

            } else {
                throw new Error("No token received");
            }
        } catch (error) {
            setError("An error occurred while generating the QR code");
            console.error("QR Code error:", error);
        }
    };

    const viewQrToken = async (userId, genUsers, nameuser) => {
        try {
            const token = checkToken();
            if (!token) return;

            const response = await fetch(
                `${API_BASE_URL}/auth/api/main/tokens/view/${userId}`,
                {
                    method: "GET",
                    headers: {
                        accept: "*/*",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const body = await response.json().catch(() => null);
                setError(body?.message || `ดู QR ไม่สำเร็จ (HTTP ${response.status})`);
                return;
            }

            const result = await response.json();
            console.log("Viewed token:", result);

            if (result.token) {
                setError("");
                setQrCodeData(result.token);
                const showQr = genUsers + ' [' + nameuser + ']';
                setGenUsers(showQr);
            } else {
                throw new Error("No token received");
            }
        } catch (error) {
            setError("An error occurred while viewing the QR code");
            console.error("View QR Code error:", error);
        }
    };

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const handleRegister = () => {
        navigate("/register");
    };

    const roleGroupMap = useMemo(() => {
        const map = {};
        catalogRoles.forEach(role => {
            map[role.name] = Array.isArray(role.groups) ? role.groups : [];
        });
        return map;
    }, [catalogRoles]);

    const getUserGroups = useCallback((user) => {
        const groupSet = new Set();
        if (Array.isArray(user.roles)) {
            user.roles.forEach(roleName => {
                (roleGroupMap[roleName] || []).forEach(g => groupSet.add(g));
            });
        }
        return Array.from(groupSet);
    }, [roleGroupMap]);

    const renderRolesCell = (user) => {
        // Old backend payload without positions/auxiliaryRoles: keep the original rendering
        // so this column doesn't break or misreport "ยังไม่มีตำแหน่ง" for data we can't read.
        if (!Array.isArray(user.positions)) {
            return Array.isArray(user.roles) ? user.roles.join(', ') : '';
        }
        const positionsLabel = user.positions.length > 0
            ? user.positions.map(p => p.name).join(', ')
            : <span style={{ color: '#9B9B9B' }}>ยังไม่มีตำแหน่ง</span>;
        const auxiliaryRoles = Array.isArray(user.auxiliaryRoles) ? user.auxiliaryRoles : [];
        return (
            <>
                <span>{positionsLabel}</span>
                {auxiliaryRoles.map(name => (
                    <span key={name} style={{
                        marginLeft: 6, fontSize: '0.8em', color: '#9B9B9B',
                        border: '1px solid #D9D9D9', borderRadius: 4, padding: '0 4px',
                    }}>{name}</span>
                ))}
            </>
        );
    };

    const NO_GROUP_LABEL = "ไม่มีกลุ่ม";

    const groupOptions = useMemo(() => {
        const groupSet = new Set();
        let hasNoGroup = false;
        users.forEach(u => {
            const groups = getUserGroups(u);
            if (groups.length === 0) {
                hasNoGroup = true;
            } else {
                groups.forEach(g => groupSet.add(g));
            }
        });
        const options = Array.from(groupSet).sort();
        if (hasNoGroup) options.push(NO_GROUP_LABEL);
        return options;
    }, [users, getUserGroups]);

    const filteredUsers = selectedGroup === 'ALL'
        ? users
        : users.filter(u => {
            const groups = getUserGroups(u);
            return selectedGroup === NO_GROUP_LABEL ? groups.length === 0 : groups.includes(selectedGroup);
        });

    return (
        <div className="btc-container">
            <h1>หน้าต่างจัดการผู้ใช้งาน</h1>
            <p style={{ color: "#6B6B6B", marginTop: -8 }}>บทบาทคือชุดความสามารถ (role capability) ไม่มีระดับบทบาท</p>
            <button className="btc-register-button" onClick={handleRegister}>
                ลงทะเบียนผู้ใช้
            </button>
            <div style={{ margin: '12px 0' }}>
                <label style={{ marginRight: 8 }}>กรองตามกลุ่ม:</label>
                <select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}>
                    <option value="ALL">ทั้งหมด</option>
                    {groupOptions.map(group => (
                        <option key={group} value={group}>{group}</option>
                    ))}
                </select>
            </div>
            {loading ? (
                <p>Loading user data...</p>
            ) : error ? (
                <p className="error">{error}</p>
            ) : (
                <table className="btc-table">
                    <thead>
                    <tr>
                        <th>IDผู้ใช้</th>
                        <th>ชื่อผู้ใช้</th>
                        <th>สถานะ</th>
                        <th>บทบาท</th>
                        <th>กลุ่ม</th>
                        <th>จัดการ</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredUsers.map(user => {
                        const missing = missingLicenses(currentUser, user);
                        const authorityTitle = missing.length > 0
                            ? `ต้องมีสิทธิ์ดูแลตำแหน่ง ${missing.join(', ')} ก่อน`
                            : undefined;
                        return (
                        <tr key={user.uid || user.id}>
                            <td>{user.username}</td>
                            <td>{user.nameuser}</td>
                            <td>{user.active ? 'Active' : 'Inactive'}</td>
                            <td>{renderRolesCell(user)}</td>
                            <td>{getUserGroups(user).join(', ') || NO_GROUP_LABEL}</td>
                            <td>
                                <RowActionsMenu
                                    actions={[
                                        {
                                            label: 'แก้ไข',
                                            icon: <i className="fas fa-edit"></i>,
                                            // Known pre-existing bug (not fixed here, out of scope for this
                                            // brief): this always navigates to /PermissionAdmin regardless
                                            // of which user's row was clicked — not parameterized by user.
                                            onClick: () => navigate("/PermissionAdmin"),
                                        },
                                        {
                                            label: 'View QR',
                                            onClick: () => viewQrToken((user.uid || user.id), user.username, user.nameuser),
                                            disabled: missing.length > 0,
                                            title: authorityTitle,
                                        },
                                        {
                                            label: 'Generate QR',
                                            onClick: () => generateQrCode((user.uid || user.id), user.username, user.nameuser),
                                            disabled: missing.length > 0,
                                            title: authorityTitle,
                                        },
                                    ]}
                                />
                            </td>
                        </tr>
                        );
                    })}
                    </tbody>

                </table>
            )}
            {qrCodeData && (
                <div className="qrCodeContainer">
                    <h2 style={{ marginBottom: 10 }}>QR Code: {genusers}</h2>
                    <div style={{ backgroundColor: "#FFFFFF", padding: "20px", display: "inline-block" }}>
                        <QRCodeCanvas
                            value={qrCodeData}
                            size={220}  // Increased size
                            bgColor={"#FFFFFF"}
                            marginSize={5}
                            fgColor={"#000000"}
                            style={{backgroundColor: "#FFFFFF", borderRadius: 8 }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
